import React, { useState, useEffect, useRef } from 'react'
import { apiFile, apiGet, apiPost } from '../../generic/Api_Functions'
import AlertUI from '../../generic/AlertUI'
import SmartNavigator from '../../generic/SmartNavigator'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import PrototypeRequestForm from './PrototypeRequestForm'
import ManufacturingRequestForm from './ManufacturingRequestForm'
import useUser from '../../generic/hooks/useUser/useUser'
import useFrontEndConfig from '../../generic/hooks/useFrontEndConfig/useFrontEndConfig'
import { formatPartReference } from './partReferenceLabel'
import useFormUtils from './useFormUtils'
import { isRenderableImageFormat, RENDERABLE_IMAGE_FORMATS_LABEL } from './renderableImageFormats'

const GeneralRequestForm = ({ setOpenForm }) => {
  const [errors, setErrors] = useState({})
  const [AlertElement, showAlert] = AlertUI()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [cesfareCodeRaw, setCesfareCodeRaw] = useState(null)
  const [orderPendingStatus, setOrderPendingStatus] = useState(null)
  const [client, setClient] = useState(null)
  const { t_ } = useSmartTranslation()
  // Historia 125 (MIC-4261): the order stores the part label in the installation's format, the
  // very same string the Manufacturing selector showed (S5).
  const [frontEndConfig] = useFrontEndConfig()
  const { user } = useUser()
  const { isEjercito } = useFormUtils()
  const closeFormTimeoutRef = useRef()

  const defaultFormData = () => ({
    type: 'FABRICATION_REQUEST',
    // IDENTIFICACIÓN
    centro: '',
    codigoSolicitud: '',
    fechaSolicitud: '',
    elaboradoPor: user.name + ' ' + user.surname,
    codigoProyecto: 'PR-DAMAD-00001',
    nombreProyecto: '',
    fechaObjetivo: null,
    prioridad: '',
    solicitanteNombre: '',
    solicitanteEmail: '',
    denominacion: '',
    pieza: '',
    partNumber: '',
    nocNin: '',
    fore: '',
    sistema: '',
    disponibilidadComercial: '',
    numeroPlano: '',
    normaEspecifica: '',
    numeroPiezasSolicitadas: '',
    causaReemplazo: '',
    descripcionEntrega: '',
    materialPropuestoCode: '',
    // SPECIFIC CENTERS
    claseBuque: '',
    nombreBuque: '',
    perteneceCUF: '',
    nocCUF: '',
    denominacionCUF: '',
    claseAvion: '',

    // GENERAL CHARACTERISTICS
    dimensionesMaximasAncho: '',
    dimensionesMaximasFondo: '',
    dimensionesMaximasAlto: '',
    peso: '',
    pesoRelevante: '',
    tolerancias: '',
    disenoModificable: '',
    certificacion: '',
    flexibilidad: '',
    color: '',
    colorRelevante: '',
    descripcionUso: '',
    posicionPieza: '',
    formaParteEnsamblaje: '',
    requierenTodosComponentes: '',

    // MATERIALS
    materialActual: '',
    materialPropuesto: { id: '', code: '', name: '' },
    tratamientoSuperficial: '',
    tipoTratamientoSuperficial: '',
    tratamientoTermico: '',
    tipoTratamientoTermico: '',
    contactoPartesMetalicas: '',
    durezaSuperficialRequerida: '',
    tecnologiaFabricacion: '',
    probetas: '',

    // WORKING CONDITIONS
    temperatura: '',
    impactos: '',
    resistenciaMecanica: '',
    transparenciaNecesaria: '',
    contactoQuimicos: '',
    intemperie: '',
    usoContactoHumano: '',
    zonasCriticas: '',

    // IMAGES
    imagenComponenteAislado: null,
    imagenComponenteEnsamblado: null,

    // NEW ORDER
    createdOrderId: ''
  })

  const [formData, setFormData] = useState({
    prototype: defaultFormData(),
    manufacturing: defaultFormData()
  })

  // Required fields for validation
  const requiredFields = [
    'elaboradoPor',
    'fechaObjetivo',
    'solicitanteNombre',
    'denominacion',
    'pieza',
    'materialPropuesto',
    'imagenComponenteAislado',
    'codigoProyecto',
    'numeroPiezasSolicitadas'
  ]

  useEffect(() => {
    const checkIfIsArmyProfile = () => {
      if (!setOpenForm) {
        return
      }
      if (isEjercito) {
        setFormData(prev => ({
          ...prev,
          prototype: {
            ...prev.prototype,
            solicitanteNombre: user.name + ' ' + user.surname,
            solicitanteEmail: user.email || ''
          },
          manufacturing: {
            ...prev.manufacturing,
            solicitanteNombre: user.name + ' ' + user.surname,
            solicitanteEmail: user.email || ''
          }
        }))
      }
    }

    checkIfIsArmyProfile()
  }, [user, setFormData, isEjercito, setOpenForm])

  useEffect(() => {
    const fetchNodeName = async () => {
      const result = await apiGet('cluster/getNodeName')
      setFormData(prev => ({
        ...prev,
        prototype: { ...prev.prototype, centro: result.message },
        manufacturing: { ...prev.manufacturing, centro: result.message }
      }))
      setCesfareCodeRaw(result.message)
    }
    fetchNodeName()
  }, [])

  useEffect(() => {
    const getEjercitoClient = async () => {
      const result = await apiGet('client/byCode/EJERCITO')
      setClient(result)
    }
    isEjercito && getEjercitoClient()
  }, [isEjercito])

  useEffect(() => {
    const fetchOrderPendingStatus = async () => {
      const orderPendingStatus = await apiGet('simpleOrderStatus/byCode/PENDING')
      setOrderPendingStatus(orderPendingStatus)
    }
    fetchOrderPendingStatus()
  }, [])

  // Clear the deferred form close scheduled after a successful submit, so the timer
  // never outlives the component (#2822).
  useEffect(() => {
    return () => {
      clearTimeout(closeFormTimeoutRef.current)
    }
  }, [])
  const generateOrderCode = async () => {
    if (!cesfareCodeRaw) {
      return {
        success: false,
        message: 'Error obteniendo el código cesfare'
      }
    }
    const cesfareCode = cesfareCodeRaw[0]
    const currentYear = new Date().getFullYear().toString()
    const minNumberLength = 9
    let nextNumber = 1
    const response = await apiGet('order/lastByYearAndMiddle/' + currentYear + '/' + cesfareCode + '/SFA'
    )
    const lastOrderCode = response?.message

    if (lastOrderCode) {
      const numberPart = lastOrderCode.slice(
        currentYear.length,
        lastOrderCode.indexOf(cesfareCode)
      )
      nextNumber = parseInt(numberPart, 10) + 1
      if (isNaN(nextNumber)) nextNumber = 1
    }
    const formattedNumber = nextNumber.toString().padStart(minNumberLength, '0')
    const newCode = currentYear + formattedNumber + cesfareCode + 'SFA'
    return newCode
  }

  const formatAndCreateOrder = async (currentFormData, partReference) => {
    try {
      const solicitudCode = await generateOrderCode()
      if (solicitudCode.message) {
        return {
          success: false,
          message: solicitudCode.message
        }
      }
      const formatedData = {
        code: solicitudCode,
        date: new Date().toISOString(),
        targetDate: currentFormData.fechaObjetivo ? new Date(currentFormData.fechaObjetivo).toISOString() : null,
        status: orderPendingStatus,
        client,
        extended_column1: cesfareCodeRaw,
        extended_column2: currentFormData.prioridad,
        extended_column4: !isEjercito ? currentFormData.elaboradoPor : '',
        extended_column5: currentFormData.codigoProyecto,
        extended_column6: currentFormData.nombreProyecto,
        extended_column7: currentFormData.solicitanteNombre,
        extended_column8: currentFormData.solicitanteEmail,
        extended_column9: currentFormData.numeroPlano,
        extended_column10: formatPartReference(partReference, frontEndConfig?.partReferenceDisplayFormat),
        // Origin and destination: this request is always issued and served by the own node
        extended_column13: cesfareCodeRaw,
        extended_column14: cesfareCodeRaw
      }

      const orderData = await apiPost('orders', formatedData)
      return { success: true, createdOrder: orderData, solicitudCode }
    } catch (error) {
      console.error('Error: ', error)
      if (
        error.message?.includes('order with the same code') ||
        error.response?.data?.message?.includes('order with the same code')
      ) {
        return {
          success: false,
          message: 'Ya existe una orden con ese código de proyecto. Por favor, utiliza un código diferente.'
        }
      }

      if (
        error.message?.includes('Delivery date must be later than the order date') || error.response?.data?.message?.includes('Delivery date must be later than the order date')
      ) {
        return {
          success: false,
          message: 'La fecha y hora objetivo deben ser posteriores a la fecha y hora actuales.'
        }
      }

      return {
        success: false,
        message: 'Error al crear la orden: ' + error?.message
      }
    }
  }

  const formatAndCreatePartReference = async (currentFormData) => {
    const defaultRawMaterialReference = await apiGet('rawMaterialsReference/byCode/default')
    const formatedData = {
      code: currentFormData.pieza,
      name: currentFormData.denominacion,
      materialType: currentFormData.materialPropuesto,
      minimumManufacturingBatch: 1,
      quantity: 0,
      rawMaterialReference: defaultRawMaterialReference,
      extended_column1: currentFormData.fore,
      extended_column2: currentFormData.sistema,
      extended_column5: currentFormData.nocNin,
      extended_column6: currentFormData.claseBuque,
      extended_column7: currentFormData.nombreBuque,
      extended_column8: currentFormData.perteneceCUF,
      extended_column9: currentFormData.nocCUF,
      extended_column10: currentFormData.denominacionCUF,
      extended_column11: currentFormData.claseAvion
    }
    let exists = false
    try {
      exists = await apiGet('partReference/exists/byCode?code=' + formatedData.code)
    } catch (error) {
      showAlert({ severity: 'warning', title: error.message })
    }
    let partReferenceData = await apiGet('partReferenceByCode/' + formatedData.code)

    if (partReferenceData && partReferenceData.code) {
      return { success: true, createdPartReference: partReferenceData }
    }
    if (exists) {
      showAlert({ severity: 'warning', title: 'La pieza existe en el repositorio, se esta descargando, por favor espere' })
      const response = await apiGet('getRequestedPartReference?partReferenceCode=' + formatedData.code)
      await apiPost('downloadReference', response)
      let shouldContinue = true
      await new Promise(resolve => setTimeout(resolve, 5000))
      const start = Date.now()
      while (shouldContinue) {
        // Check timeout
        if (Date.now() - start > 120000) {
          showAlert({ severity: 'error', title: 'La solicitud de descarga de referencia ha excedido el limite' })
        }

        const res = await apiGet('reference/downloading')
        shouldContinue = res.working

        if (shouldContinue) {
          await new Promise((resolve) => setTimeout(resolve, 1000)) // 1 sec interval
        }
      }
      // Wait until part reference is saved
      let ref = await apiGet('partReferenceByCode/' + formatedData.code)
      while (!ref) {
        ref = await apiGet('partReferenceByCode/' + formatedData.code)
        await new Promise((resolve) => setTimeout(resolve, 1000)) // 1 sec interval
        if (Date.now() - start > 10000) {
          showAlert({ severity: 'error', title: 'Algo ha ocurrido al persistir la referencia de pieza' })
        }
      }
      partReferenceData = ref
    } else {
      const createdPartReference = await apiPost('partReferences', formatedData)
      // Re-leemos por código para obtener el mismo DTO fiable ({id, code, name}) que el camino de
      // pieza existente; así la subida de documentos (imagen principal) recibe un id válido.
      const refByCode = await apiGet('partReferenceByCode/' + formatedData.code)
      partReferenceData = (refByCode && refByCode.id) ? refByCode : createdPartReference
    }
    return { success: true, createdPartReference: partReferenceData }
  }

  const formateAndCreateLineOrder = async (currentFormData, orderId, partReference) => {
    try {
      const formatedData = {
        lineNumber: 1,
        partReference,
        quantity: currentFormData.numeroPiezasSolicitadas ? parseInt(currentFormData.numeroPiezasSolicitadas) : 0,
        targetDate: currentFormData.fechaObjetivo ? new Date(currentFormData.fechaObjetivo).toISOString() : null,
        comment: currentFormData.descripcionEntrega
      }
      await apiPost('order/' + orderId + '/lineOrder', formatedData)
    } catch (error) {
      console.error('Error creando linea de orden:', error)
    }
  }
  const validateForm = (currentFormData, checkImages) => {
    const newErrors = {}
    let requiredFieldsCopy = [...requiredFields]
    if (!checkImages) {
      // Manufacturing tab (#2912): it has no image input and no material control either -- the
      // material is derived from the chosen part reference, and Sun answers it as null when the
      // reference has none. Requiring it there blocks the request on a field the user cannot see.
      requiredFieldsCopy = requiredFieldsCopy.filter(field => field !== 'imagenComponenteAislado' && field !== 'materialPropuesto')
    }
    requiredFieldsCopy.forEach(field => {
      if (field === 'fechaObjetivo') {
        if (!currentFormData[field]) {
          newErrors[field] = 'La fecha objetivo es obligatoria'
        }
      } else if (field === 'imagenComponenteAislado') {
        if (!currentFormData[field]) {
          newErrors[field] = 'La imagen del componente aislado es obligatoria'
        }
      } else if (field === 'materialPropuesto') {
        if (!currentFormData[field]?.code && !currentFormData[field]?.id) {
          newErrors[field] = 'El material propuesto es obligatorio'
        }
      } else if (field.includes('.')) {
        const [parent, child] = field.split('.')
        if (!currentFormData[parent]?.[child]?.trim()) {
          newErrors[field] = 'Este campo es obligatorio'
        }
      } else {
        if (!currentFormData[field]?.trim()) {
          newErrors[field] = 'Este campo es obligatorio'
        }
      }
    })

    // Email validation
    if (currentFormData.solicitanteEmail && !/\S+@\S+\.\S+/.test(currentFormData.solicitanteEmail)) {
      newErrors.solicitanteEmail = 'Email inválido'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Límite de tamaño de fichero configurado en la aplicación (MAX_FILE_SIZE, en MB). Devuelve el
  // límite en bytes, o null si no se puede determinar (en ese caso no se pre-valida).
  const getMaxFileSizeBytes = async () => {
    try {
      const maxSize = await apiGet('settings/MAX_FILE_SIZE')
      const size = maxSize?.value
      if (size === null || size === undefined) {
        return null
      }
      return parseInt(size) * 1024 * 1024
    } catch (error) {
      return null
    }
  }

  const findInvalidFormatImage = (currentFormData) => {
    return ['imagenComponenteAislado', 'imagenComponenteEnsamblado']
      .map((key) => currentFormData[key])
      .find((file) => file instanceof File && !isRenderableImageFormat(file))
  }

  const handleSubmit = async (e, formType) => {
    e.preventDefault()

    const currentFormData = formData[formType]
    const checkImages = formType === 'prototype'

    if (!validateForm(currentFormData, checkImages)) {
      showAlert({ severity: 'error', title: 'Por favor, completa todos los campos obligatorios marcados con *' })
      return
    }

    // Validación de tamaño de imagen ANTES de crear pieza/OF, para no dejar huérfanos y dar un
    // mensaje claro. El proxy (nginx) puede tener un límite propio y devolver 413 al subir; ese
    // caso se maneja en el catch de más abajo.
    const maxFileSizeBytes = await getMaxFileSizeBytes()
    if (maxFileSizeBytes) {
      const oversizedImage = ['imagenComponenteAislado', 'imagenComponenteEnsamblado']
        .map((key) => currentFormData[key])
        .find((file) => file instanceof File && file.size > maxFileSizeBytes)
      if (oversizedImage) {
        const maxMb = Math.round(maxFileSizeBytes / (1024 * 1024))
        showAlert({
          severity: 'error',
          title: t_('The image "{{name}}" exceeds the maximum allowed size ({{maxSize}} MB). Reduce or compress it and try again.',
            { name: oversizedImage.name, maxSize: maxMb })
        })
        return
      }
    }

    // Guard de formato ANTES de crear nada: la imagen debe pertenecer a la allow-list (JPG/PNG) que
    // el motor de informes siempre renderiza. Se comprueba el fichero real (MIME y/o extensión), no
    // el atributo accept del input (filtro de UI, no barrera; puede ignorarse o saltarse por
    // arrastre/diálogo del sistema). Se añade junto al guard de tamaño existente, sin sustituirlo.
    const invalidFormatFile = findInvalidFormatImage(currentFormData)
    if (invalidFormatFile) {
      const isAisladoInvalid = currentFormData.imagenComponenteAislado === invalidFormatFile
      showAlert({
        severity: 'error',
        title: isAisladoInvalid
          ? t_('The image {{name}} (isolated component) has an unsupported format. Allowed formats: {{formats}}. Choose a different file and try again.',
            { name: invalidFormatFile.name, formats: RENDERABLE_IMAGE_FORMATS_LABEL })
          : t_('The image {{name}} (assembled component) has an unsupported format. Allowed formats: {{formats}}. Choose a different file and try again.',
            { name: invalidFormatFile.name, formats: RENDERABLE_IMAGE_FORMATS_LABEL })
      })
      return
    }

    try {
      const formDataToSend = new FormData()
      const reportData = {}
      Object.keys(currentFormData).forEach(key => {
        if (key === 'imagenComponenteAislado' || key === 'imagenComponenteEnsamblado') {
          if (currentFormData[key]) {
            formDataToSend.append(key, currentFormData[key])
          }
        } else if (key === 'fechaObjetivo' && currentFormData[key]) {
          const dateValue = currentFormData[key].toISOString()
          formDataToSend.append(key, dateValue)
          reportData[key] = dateValue
        } else {
          formDataToSend.append(key, currentFormData[key])
          reportData[key] = currentFormData[key]
        }
      })
      reportData.fechaSolicitud = formatDateToReport(new Date())
      const partReferenceResult = formType === 'manufacturing' ? { success: true, createdPartReference: formData.manufacturing.partReference } : await formatAndCreatePartReference(currentFormData)
      if (!partReferenceResult.success) {
        showAlert({
          severity: 'error',
          title: partReferenceResult.message || 'Error al crear la pieza'
        })
        return
      }
      const orderResult = await formatAndCreateOrder(currentFormData, partReferenceResult.createdPartReference)
      if (!orderResult.success) {
        showAlert({
          severity: 'error',
          title: orderResult.message || 'Error al crear la orden'
        })
        return
      }
      reportData.fechaObjetivo = formatDateToReport(new Date(reportData.fechaObjetivo))
      reportData.codigoSolicitud = orderResult.solicitudCode

      formateAndCreateLineOrder(currentFormData, orderResult.createdOrder.id, partReferenceResult.createdPartReference)
      setIsSubmitting(true)
      // La subida de documentos puede fallar (p. ej. 413 del proxy si la imagen supera su límite);
      // por eso el mensaje de éxito se muestra DESPUÉS de subir, no antes.
      await uploadDocuments(partReferenceResult.createdPartReference.id, formDataToSend)
      if (currentFormData.imagenComponenteAislado instanceof File) {
        reportData.imagenComponenteAislado = await fileToBase64(currentFormData.imagenComponenteAislado)
      }
      if (currentFormData.imagenComponenteEnsamblado instanceof File) {
        reportData.imagenComponenteEnsamblado = await fileToBase64(currentFormData.imagenComponenteEnsamblado)
      }
      reportData.createdOrderId = orderResult.createdOrder.id
      // El éxito y el cierre del formulario se muestran/programan SOLO tras confirmar (await) la
      // respuesta del PDF: si "report/fabricationRequest" fallase, cae al catch de abajo y no se
      // anuncia éxito ni se cierra el formulario (RN7).
      await apiPost('report/fabricationRequest', reportData)
      showAlert({
        severity: 'success',
        title: 'Solicitud registrada correctamente'
      })
      clearTimeout(closeFormTimeoutRef.current)
      closeFormTimeoutRef.current = setTimeout(() => {
        setOpenForm(false)
      }, 2000)
    } catch (error) {
      console.error('Error in handleSubmit:', error)
      const message = error?.message || ''
      const isFileTooLarge = error?.status === 413 || /too large|exceeds the maximum/i.test(message)
      showAlert({
        severity: 'error',
        title: isFileTooLarge
          ? t_('The image is too large to upload. Reduce or compress it and try again.')
          : t_('Unexpected error while processing the form')
      })
      setIsSubmitting(false)
    }
  }
  function formatDateToReport (date) {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: timezone
    }).replace(/\//g, '-')
  }
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new window.FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => {
        const base64 = reader.result.split(',')[1]
        resolve(base64)
      }
      reader.onerror = error => reject(error)
    })
  }
  const uploadDocuments = async (partReferenceId, formDataToSend) => {
    const documentType = { id: 1, code: 'IMAGE', name: 'Image' }

    const files = [
      { file: formDataToSend.get('imagenComponenteAislado'), description: 'Imagen del componente aislado', asMainImage: true },
      { file: formDataToSend.get('imagenComponenteEnsamblado'), description: 'Imagen del componente ensamblado', asMainImage: false }
    ]

    for (const { file, description, asMainImage } of files) {
      if (file) {
        const documentData = {
          description,
          documentType,
          documentName: file.name,
          order: 10
        }
        const url = 'document/create/PART_REFERENCE/' + partReferenceId + (asMainImage ? '?asMainImage=true' : '')
        await apiFile(url, documentData, file)
      }
    }
  }

  const componentsInfo = [
    {
      title: t_('Prototype'),
      component: (
        <PrototypeRequestForm
          formData={formData.prototype}
          setFormData={(data) => setFormData(prev => ({ ...prev, prototype: typeof data === 'function' ? data(prev.prototype) : data }))}
          remoteErrors={errors}
          onSubmit={(e) => handleSubmit(e, 'prototype')}
          isSubmitting={isSubmitting}
        />
      )
    },
    {
      title: t_('Manufacturing'),
      component: (
        <ManufacturingRequestForm
          formData={formData.manufacturing}
          setFormData={(data) => setFormData(prev => ({ ...prev, manufacturing: typeof data === 'function' ? data(prev.manufacturing) : data }))}
          remoteErrors={errors}
          onSubmit={(e) => handleSubmit(e, 'manufacturing')}
          isSubmitting={isSubmitting}
        />
      )
    }
  ]

  // The hosting dialog drops its top padding (contentSx in Order.jsx) so the sticky tab bar can
  // stick flush under the modal header: Chrome shrinks the sticky area by the scroller's padding.
  return (
    <>
      <div style={{ position: 'sticky', top: 0, zIndex: 9999 }}>{AlertElement}</div>
      <SmartNavigator componentsInfo={componentsInfo} />
    </>
  )
}

export default GeneralRequestForm
